import { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../utils/config';
import { LegalDocumentType } from '../models/LegalDocuments/legal-document-type.enum';
import { LegalDocumentTemplate } from '../models/LegalDocuments/legal-document-template';
import PrivacyPolicyPdf from '../assets/documents/Privacy_Policy_v2-12-2023.pdf';
import TermsOfServicePdf from '../assets/documents/TERMS_OF_SERVICE_12.9.2025.pdf';
import connectBuyersTosAddendumPdf from '../assets/documents/Buyer_Terms_of_Service_1-26-2026.pdf';

const PUBLIC_ENDPOINT_MAP: Record<LegalDocumentType, string> = {
    [LegalDocumentType.PrivacyPolicy]: '/api/public/legal-documents/privacy-policy',
    [LegalDocumentType.TermsOfService]: '/api/public/legal-documents/terms-of-service',
    [LegalDocumentType.BuyersTermsOfService]: '/api/public/legal-documents/buyers-terms-of-service',
};

const FALLBACK_PDF_MAP: Record<LegalDocumentType, string> = {
    [LegalDocumentType.PrivacyPolicy]: PrivacyPolicyPdf,
    [LegalDocumentType.TermsOfService]: TermsOfServicePdf,
    [LegalDocumentType.BuyersTermsOfService]: connectBuyersTosAddendumPdf,
};

interface UseLegalDocumentResult {
    document: LegalDocumentTemplate | null;
    documentUrl: string | null;
    isLoading: boolean;
    error: string | null;
}

export const useLegalDocument = (
    documentType: LegalDocumentType,
    authAxios?: any
): UseLegalDocumentResult => {
    const [document, setDocument] = useState<LegalDocumentTemplate | null>(null);
    const [documentUrl, setDocumentUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchDocument = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const endpoint = PUBLIC_ENDPOINT_MAP[documentType];
                let response;

                if (authAxios) {
                    response = await authAxios.get(endpoint);
                } else {
                    const baseUrl = BASE_URL === '/' ? '' : BASE_URL;
                    response = await axios.get(`${baseUrl}${endpoint}`);
                }

                if (response.status === 200 && response.data) {
                    const url = typeof response.data === 'string' 
                        ? response.data 
                        : (response.data.downloadUrl || response.data.url);
                    
                    if (typeof response.data === 'object') {
                        setDocument(response.data);
                    }
                    setDocumentUrl(url || FALLBACK_PDF_MAP[documentType]);
                } else {
                    setDocumentUrl(FALLBACK_PDF_MAP[documentType]);
                }
            } catch (err: any) {
                console.error('Error fetching legal document, using fallback:', err);
                setDocumentUrl(FALLBACK_PDF_MAP[documentType]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDocument();
    }, [documentType, authAxios]);

    return { document, documentUrl, isLoading, error };
};
